import { omit } from "lodash";
import type { ComponentPropsWithRef } from "react";
import { forwardRef, useMemo } from "react";

import { classNames } from "@/react/helpers/twMergeWithCN";
import type { FieldElement } from "@/react/types/Element";

import { ChoiceElement as ChoiceElementComponent } from "../../blocks/shared/components/ChoiceElement";
import { useProfileFieldPrefill } from "../shared/useProfileFieldPrefill";

interface MultipleChoiceBlockMeta {
  elementableId: number;
  elementId: number;
  fieldIndex: number;
  position: number;
  profileFieldId?: number | null;
}

interface Option {
  id: string;
  value: string;
}

export interface FormMultipleChoiceBlockProps extends ComponentPropsWithRef<"div"> {
  blockInstanceMeta?: MultipleChoiceBlockMeta;
  label?: string;
  options?: Option[];
  required?: boolean;
}

export const FormMultipleChoiceBlock = forwardRef<
  HTMLDivElement,
  FormMultipleChoiceBlockProps
>(
  (
    {
      label = "Multiple choice",
      required = false,
      options = [
        { id: "1", value: "Option 1" },
        { id: "2", value: "Option 2" },
        { id: "3", value: "Option 3" },
      ],
      className,
      blockInstanceMeta = {},
      ...rest
    },
    ref,
  ) => {
    const elementProps = omit(rest, ["blockPath"]);
    const { context, rawPrefillValue } =
      useProfileFieldPrefill(blockInstanceMeta);
    const fallbackIndex = useMemo(() => Date.now(), []);
    const prefillValue = rawPrefillValue;

    const optionValues = options.map(option => option.value);

    const element: FieldElement = {
      id: blockInstanceMeta.elementId ?? 0,
      elementable_id: blockInstanceMeta.elementableId ?? 0,
      position: blockInstanceMeta.position ?? 0,
      type: "field",
      attributes: {
        field_type: "multiple_choice",
        label,
        placeholder: null,
        required,
        hidden: false,
        options: optionValues,
      },
    };

    const errors =
      "backendErrors" in context ? (context.backendErrors ?? {}) : {};

    return (
      <div
        ref={ref}
        className={classNames("w-full", className)}
        {...elementProps}
      >
        <ChoiceElementComponent
          element={element}
          fieldIndex={blockInstanceMeta.fieldIndex ?? fallbackIndex}
          errors={errors}
          defaultValue={prefillValue}
        />
      </div>
    );
  },
);

FormMultipleChoiceBlock.displayName = "FormMultipleChoiceBlock";
